import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Lock } from "lucide-react";
import { Reveal, Overline, MaskedLine, FadeIn } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "All",
  "T-Shirts",
  "Overshirts",
  "Hoodies & Sweatshirts",
  "Jackets & Outerwear",
  "Trousers & Shorts",
  "Hats",
  "Socks",
];

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [selected, setSelected] = useState(null);
  const [size, setSize] = useState("M");
  const [quantity, setQuantity] = useState(1);
  const [showBack, setShowBack] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [preview, setPreview] = useState(false);
  const [adminKey, setAdminKey] = useState("");
  const [showKeyInput, setShowKeyInput] = useState(false);

  useEffect(() => {
    document.title = "Shop — ADD VALUE Clothing";
    loadPublic();
  }, []);

  const loadPublic = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch {
      setProducts([]);
    }
  };

  const loadPreview = async (e) => {
    e?.preventDefault();
    try {
      const res = await axios.get(`${API}/admin/products`, {
        headers: { "x-admin-key": adminKey },
      });
      setProducts(res.data);
      setPreview(true);
      setShowKeyInput(false);
      toast.success("Owner preview active.");
    } catch {
      toast.error("Invalid admin key.");
    }
  };

  const visible = useMemo(
    () => products.filter((p) => category === "All" || p.category === category),
    [products, category]
  );

  const openProduct = (p) => {
    setSelected(p);
    setSize(p.sizes?.length > 1 ? p.sizes[1] : p.sizes?.[0] || "M");
    setQuantity(1);
    setShowBack(false);
  };

  const buy = async () => {
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/shop/checkout`, {
        slug: selected.slug,
        size,
        quantity,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Checkout is not available for this piece yet.");
      setCheckingOut(false);
    }
  };

  return (
    <div data-testid="shop-page">
      {/* HERO */}
      <section className="relative flex min-h-[60vh] items-end overflow-hidden pb-16 pt-40">
        <div className="relative mx-auto w-full max-w-7xl px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <Overline className="!text-stone-300 [&>span]:!bg-stone-400/70">
              ADD VALUE Clothing — The Shop
            </Overline>
          </FadeIn>
          <h1 className="mt-8 font-serif text-4xl uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Collection</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="italic text-stone-300">001</span>
            </MaskedLine>
          </h1>
          <FadeIn delay={0.6} className="mt-8 max-w-xl">
            <p className="text-sm font-light leading-relaxed text-stone-400 sm:text-base">
              A coordinated luxury collection — T-shirts, overshirts, hoodies,
              knitwear, tailoring, headwear, and essentials — designed in black
              and white, released together.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* CATEGORY FILTER */}
      <section className="border-t border-[#1c1c1f]">
        <div className="mx-auto max-w-7xl px-6 py-10 sm:px-8 lg:px-12">
          <div className="flex flex-wrap gap-3" data-testid="shop-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`shop-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                  category === c
                    ? "border-stone-300 text-white"
                    : "border-[#2a2a2e] text-stone-400 hover:border-stone-500 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="mx-auto max-w-7xl px-6 pb-24 sm:px-8 lg:px-12 lg:pb-32">
        {visible.length === 0 ? (
          <Reveal className="border border-dashed border-[#2a2a2e] p-12 text-center lg:p-20">
            <p
              data-testid="shop-empty-state"
              className="font-serif text-2xl italic leading-snug text-stone-400 sm:text-3xl"
            >
              {products.length === 0 && !preview
                ? "The shop opens when Collection 001 is approved for release."
                : "This category is in development."}
            </p>
            {products.length === 0 && !preview && (
              <Link
                data-testid="shop-waitlist-link"
                to="/clothing"
                className="group mt-8 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-200"
              >
                Join the waitlist
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
              </Link>
            )}
          </Reveal>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {visible.map((p, i) => (
              <Reveal key={p.slug} delay={0.06 * i}>
                <button
                  data-testid={`product-card-${p.slug}`}
                  onClick={() => openProduct(p)}
                  className="group block w-full border border-[#222226] bg-[#121214] text-left transition-colors duration-500 hover:border-stone-400/50"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="aspect-[3/4] w-full object-cover grayscale transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    {!p.published && (
                      <span className="absolute left-4 top-4 border border-stone-500/60 bg-[#0A0A0B]/85 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-stone-300">
                        Pending Approval
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-stone-500">
                      {p.category}
                    </p>
                    <h3 className="mt-2 font-serif text-xl text-white">{p.name}</h3>
                    <p className="mt-3 font-mono text-xs text-stone-300">
                      {p.price ? `$${p.price.toFixed(2)}` : "Price on approval"}
                    </p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}

        {/* OWNER PREVIEW */}
        <div className="mt-16 border-t border-[#1c1c1f] pt-8">
          {!preview && !showKeyInput && (
            <button
              data-testid="shop-preview-toggle"
              onClick={() => setShowKeyInput(true)}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-stone-600 transition-colors duration-300 hover:text-stone-300"
            >
              <Lock className="h-3.5 w-3.5" /> Owner preview
            </button>
          )}
          {!preview && showKeyInput && (
            <form data-testid="shop-preview-form" onSubmit={loadPreview} className="flex max-w-sm gap-3">
              <input
                data-testid="shop-preview-key-input"
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Admin key"
                className="w-full border border-[#2a2a2e] bg-[#121214] px-4 py-3 text-sm text-white placeholder:text-stone-600 focus:border-stone-400 focus:outline-none"
              />
              <button
                data-testid="shop-preview-submit"
                type="submit"
                className="shrink-0 border border-[#2a2a2e] px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-stone-300 transition-colors duration-300 hover:border-stone-400 hover:text-white"
              >
                View
              </button>
            </form>
          )}
          {preview && (
            <p data-testid="shop-preview-banner" className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-300">
              Owner preview — unpublished pieces visible. Manage pricing & publishing at /admin.
            </p>
          )}
        </div>
      </section>

      {/* PRODUCT MODAL */}
      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="product-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-end justify-center bg-[#0A0A0B]/90 backdrop-blur-md sm:items-center"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="grid max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-[#2a2a2e] bg-[#0d0d0f] sm:grid-cols-2"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={showBack && selected.back_image ? selected.back_image : selected.image}
                  alt={selected.name}
                  className="h-64 w-full object-cover grayscale sm:h-full"
                />
                {selected.back_image && (
                  <div className="absolute bottom-4 left-4 flex gap-2">
                    <button
                      data-testid="view-front-button"
                      onClick={() => setShowBack(false)}
                      className={`border px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                        !showBack ? "border-white bg-[#0A0A0B]/85 text-white" : "border-[#2a2a2e] bg-[#0A0A0B]/60 text-stone-400 hover:text-white"
                      }`}
                    >
                      Front
                    </button>
                    <button
                      data-testid="view-back-button"
                      onClick={() => setShowBack(true)}
                      className={`border px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                        showBack ? "border-white bg-[#0A0A0B]/85 text-white" : "border-[#2a2a2e] bg-[#0A0A0B]/60 text-stone-400 hover:text-white"
                      }`}
                    >
                      Back Print
                    </button>
                  </div>
                )}
              </div>
              <div className="flex flex-col p-8 lg:p-10">
                <div className="flex items-start justify-between">
                  <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-stone-400">
                    {selected.category} — Collection 001
                  </p>
                  <button
                    data-testid="product-modal-close"
                    onClick={() => setSelected(null)}
                    aria-label="Close"
                    className="text-stone-500 transition-colors duration-300 hover:text-white"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <h3 className="mt-4 font-serif text-3xl text-white">{selected.name}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-stone-400">
                  {selected.description}
                </p>
                <p className="mt-5 font-serif text-2xl text-white">
                  {selected.price ? `$${selected.price.toFixed(2)}` : "Price on approval"}
                </p>

                {selected.published ? (
                  <>
                    <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">
                      Size
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="size-selector">
                      {selected.sizes.map((s) => (
                        <button
                          key={s}
                          data-testid={`size-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={() => setSize(s)}
                          className={`h-10 min-w-12 border px-2 font-mono text-xs transition-colors duration-300 ${
                            size === s
                              ? "border-stone-300 text-white"
                              : "border-[#2a2a2e] text-stone-400 hover:border-stone-500"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">
                      Quantity
                    </p>
                    <div className="mt-3 flex items-center gap-4">
                      <button
                        data-testid="quantity-decrease"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="h-10 w-10 border border-[#2a2a2e] text-stone-300 transition-colors duration-300 hover:border-stone-400"
                      >
                        −
                      </button>
                      <span data-testid="quantity-value" className="w-6 text-center font-mono text-sm text-white">
                        {quantity}
                      </span>
                      <button
                        data-testid="quantity-increase"
                        onClick={() => setQuantity(Math.min(10, quantity + 1))}
                        className="h-10 w-10 border border-[#2a2a2e] text-stone-300 transition-colors duration-300 hover:border-stone-400"
                      >
                        +
                      </button>
                    </div>
                    <button
                      data-testid="buy-now-button"
                      onClick={buy}
                      disabled={checkingOut}
                      className="mt-10 inline-flex items-center justify-center gap-3 bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-stone-300 disabled:opacity-50"
                    >
                      {checkingOut ? "Preparing checkout…" : "Buy — Secure Checkout"}
                      <ArrowUpRight className="h-4 w-4" />
                    </button>
                    <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.2em] text-stone-600">
                      Card payment by Stripe. Shipping calculated at checkout.
                    </p>
                  </>
                ) : (
                  <p
                    data-testid="product-pending-note"
                    className="mt-10 border border-dashed border-[#2a2a2e] p-5 text-sm font-light text-stone-500"
                  >
                    This piece is awaiting final approval — design, photography,
                    pricing, and inventory. It cannot be purchased yet.
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Shop;
